import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid2, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import useNotify from '../hooks/useNotify';
import formatErrorToString from '../helpers/formatErrorToString';
import CommonButton from '../components/atom/CommonButton';
import FormikPasswordInput from '../components/atom/FormikPasswordInput';
import { useTranslation } from 'react-i18next';
import { useResetPassword } from '@openapi/auth/auth';

function ResetPasswordModal() {
    const { t } = useTranslation();
    const { token } = useParams();
    const navigate = useNavigate();
    const { notifySuccess } = useNotify();
    const resetPasswordMutation = useResetPassword();

    const changePassword = async (tokenParam, password, setSubmitting, setErrors) => {
        setErrors({});
        resetPasswordMutation.reset();
        try {
            await resetPasswordMutation.mutateAsync({
                data: {
                    token: tokenParam,
                    password,
                },
            });
            notifySuccess(t('login.passwordModified'));
            navigate('/');
        } catch (error) {
            setErrors({ api: formatErrorToString(error) });
        } finally {
            setSubmitting(false);
        }
    };
    useEffect(() => {
        if (token?.length !== 32) {
            navigate('/');
        }
    }, [navigate, token]);

    const validateSchema = Yup.object().shape({
        password: Yup.string()
            .min(6, t('login.passwordRequirements.length'))
            // .matches(/[0-9]/, t('login.passwordRequirements.number'))
            // .matches(/[a-z]/, t('login.passwordRequirements.lowercase'))
            // .matches(/[A-Z]/, t('login.passwordRequirements.uppercase'))
            .required(t('common.required')),
        confirm: Yup.string()
            .oneOf([Yup.ref('password')], t('login.passwordRequirements.twoPasswordsMatch'))
            .required(t('common.required')),
    });

    return (
        <Dialog aria-labelledby="sign-in-modal" maxWidth="xs" open>
            <DialogTitle>{t('login.newPasswordTitle')}</DialogTitle>
            <Formik
                validationSchema={validateSchema}
                initialValues={{ password: '', confirm: '' }}
                onSubmit={({ password }, { setSubmitting, setErrors }) => {
                    changePassword(token, password, setSubmitting, setErrors);
                }}
            >
                {({ isSubmitting, errors, isValid, dirty }) => (
                    <Form>
                        <DialogContent>
                            <Grid2 container>
                                <Grid2 size={{ xs: 12 }}>
                                    {errors.api && <Alert severity="error">{errors.api}</Alert>}
                                </Grid2>
                                <FormikPasswordInput
                                    name="password"
                                    label={t('login.newPassword')}
                                />
                                <FormikPasswordInput
                                    name="confirm"
                                    label={t('login.confirmPassword')}
                                />
                            </Grid2>
                        </DialogContent>
                        <DialogActions>
                            <CommonButton
                                type="submit"
                                disabled={
                                    isSubmitting ||
                                    !(isValid && dirty) ||
                                    resetPasswordMutation.isPending
                                }
                                label={t('login.resetPassword')}
                                buttonType="primary"
                            />
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}

export default ResetPasswordModal;
